import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, Calendar } from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

export function BundlePerformanceChart() {
  const { data: performanceData, isLoading } = useQuery({
    queryKey: ['bundle-performance'],
    queryFn: async () => {
      // Get line items with bundle_id from the last 6 months
      const sixMonthsAgo = subMonths(new Date(), 6);
      
      const { data: lineItems, error } = await supabase
        .from('deal_line_items')
        .select(`
          bundle_id,
          total_price,
          created_at,
          deal:deals(status)
        `)
        .not('bundle_id', 'is', null)
        .gte('created_at', sixMonthsAgo.toISOString())
        .order('created_at');

      if (error) throw error;

      // Get bundles for name mapping
      const { data: bundles } = await supabase
        .from('product_bundles')
        .select('id, name');

      const bundleMap = new Map(bundles?.map(b => [b.id, b.name]) || []);

      // Group by month and bundle
      const monthlyData: Record<string, Record<string, number>> = {};
      const bundleIds = new Set<string>();

      lineItems?.forEach((item: any) => {
        if (!item.bundle_id) return;
        bundleIds.add(item.bundle_id);
        
        const monthKey = format(new Date(item.created_at), 'yyyy-MM');
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {};
        }
        monthlyData[monthKey][item.bundle_id] = 
          (monthlyData[monthKey][item.bundle_id] || 0) + item.total_price;
      });

      // Generate chart data for last 6 months
      const chartData = [];
      for (let i = 5; i >= 0; i--) {
        const month = subMonths(new Date(), i);
        const monthKey = format(month, 'yyyy-MM');
        const monthLabel = format(month, 'MMM yyyy');
        
        const dataPoint: any = { month: monthLabel };
        bundleIds.forEach(bundleId => {
          const bundleName = bundleMap.get(bundleId) || 'Unknown';
          dataPoint[bundleName] = monthlyData[monthKey]?.[bundleId] || 0;
        });
        chartData.push(dataPoint);
      }

      // Calculate totals per bundle for bar chart
      const bundleTotals = Array.from(bundleIds).map(bundleId => {
        const total = Object.values(monthlyData).reduce((sum, monthData) => {
          return sum + (monthData[bundleId] || 0);
        }, 0);
        return {
          name: bundleMap.get(bundleId) || 'Unknown',
          revenue: total,
        };
      }).sort((a, b) => b.revenue - a.revenue);

      return {
        trendData: chartData,
        totalsData: bundleTotals,
        bundleNames: Array.from(bundleIds).map(id => bundleMap.get(id) || 'Unknown'),
      };
    },
  });

  const colors = ['hsl(var(--primary))', '#22c55e', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
          <CardContent><Skeleton className="h-64 w-full" /></CardContent>
        </Card>
        <Card>
          <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
          <CardContent><Skeleton className="h-64 w-full" /></CardContent>
        </Card>
      </div>
    );
  }

  const hasTrendData = performanceData?.trendData?.some(d => 
    Object.keys(d).filter(k => k !== 'month').some(k => d[k] > 0)
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Bundle Revenue Trends (6 Months)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasTrendData ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceData?.trendData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="month" 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
                />
                <Legend />
                {performanceData?.bundleNames.map((name, idx) => (
                  <Line 
                    key={name}
                    type="monotone" 
                    dataKey={name} 
                    stroke={colors[idx % colors.length]}
                    strokeWidth={2}
                    dot={{ fill: colors[idx % colors.length], strokeWidth: 2 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground border border-dashed rounded-lg">
              <div className="text-center">
                <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No bundle revenue data available</p>
                <p className="text-sm">Start using bundles in deals to see trends</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Total Revenue by Bundle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Total Revenue by Bundle
          </CardTitle>
        </CardHeader>
        <CardContent>
          {performanceData?.totalsData && performanceData.totalsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData.totalsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                <XAxis 
                  type="number" 
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={120}
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']}
                />
                <Bar 
                  dataKey="revenue" 
                  fill="hsl(var(--primary))" 
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground border border-dashed rounded-lg">
              <div className="text-center">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No bundle revenue data</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
